import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Package, ArrowRight, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PortalLayout from "@/components/PortalLayout";
import { api } from "@/lib/api";
import { formatUSD, STAGES } from "@/lib/constants";

const WORK_STATUS = {
  pending_payment: { key: "portal.statusPendingPayment", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  in_progress: { key: "portal.statusInProgress", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  completed: { key: "portal.statusCompleted", cls: "bg-orange-100 text-orange-700 border-orange-200" },
};
const PAY_STATUS = {
  paid: { key: "portal.statusPaid", cls: "bg-green-100 text-green-700 border-green-200" },
  pending: { key: "portal.statusUnpaid", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  failed: { key: "portal.statusFailed", cls: "bg-red-100 text-red-700 border-red-200" },
};

export default function Orders() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get("/requests")
      .then((r) => setRequests(r.data.requests || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PortalLayout active="orders">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("portal.orders")}</h1>
            <p className="mt-1.5 text-muted-foreground">{t("portal.ordersSubtitle")}</p>
          </div>
          <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/company-formation")} data-testid="orders-new">{t("portal.newOrder")}</Button>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center card-soft" data-testid="orders-loading">
              <Loader2 className="h-6 w-6 mx-auto animate-spin text-orange-700" />
              <p className="mt-3 text-sm text-muted-foreground">{t("common.loading")}</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-12 text-center card-soft" data-testid="orders-error">
              <AlertCircle className="h-7 w-7 mx-auto text-red-600" />
              <p className="mt-3 font-heading text-lg text-foreground">{t("portal.ordersError")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("forms.errorGeneric")}</p>
              <Button className="mt-5 rounded-sm" variant="outline" onClick={load} data-testid="orders-retry"><RefreshCw className="h-4 w-4 mr-1.5" /> {t("common.retry")}</Button>
            </div>
          ) : requests.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center card-soft" data-testid="orders-empty">
              <Package className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-4 font-heading text-xl text-foreground">{t("portal.noOrders")}</p>
              <p className="mt-1.5 text-sm text-muted-foreground">{t("portal.noOrdersBody")}</p>
              <Button className="mt-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/company-formation")} data-testid="orders-empty-cta">{t("common.browseAll")} <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {requests.map((r, i) => {
                const ws = WORK_STATUS[r.status] || WORK_STATUS.pending_payment;
                const ps = PAY_STATUS[r.payment_status] || PAY_STATUS.pending;
                return (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}
                    onClick={() => navigate(`/requests/${r.id}`)}
                    className="rounded-xl border border-border bg-card p-6 card-soft card-hover cursor-pointer" data-testid={`orders-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono" data-testid={`orders-ref-${r.id}`}>{r.id}</p>
                        <h3 className="mt-1 font-heading text-xl tracking-tight text-foreground">{r.package_name}</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {r.company_name}{r.jurisdiction ? ` · ${r.jurisdiction}` : ""}
                          {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <p className="font-heading text-lg text-foreground">{formatUSD(r.amount)}</p>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${ws.cls}`} data-testid={`orders-work-${r.id}`}>{t(ws.key)}</Badge>
                          <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${ps.cls}`} data-testid={`orders-pay-${r.id}`}>{t(ps.key)}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-end justify-between gap-4 flex-wrap border-t border-border pt-4">
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("portal.nextAction")}</p>
                        <p className="text-sm text-foreground mt-0.5">{r.next_action || (r.stage < STAGES.length ? STAGES[r.stage] : "Delivered")}</p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={(e) => { e.stopPropagation(); navigate(`/requests/${r.id}`); }} data-testid={`orders-view-${r.id}`}>{t("common.viewDetails")} <ArrowRight className="h-4 w-4 ml-1" /></Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
